'use client';

import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCheckCircle,
    faExclamationTriangle,
    faInfoCircle,
    faXmark
} from "@fortawesome/free-solid-svg-icons";
import clsx from "clsx";

export type ToastType = "success" | "error" | "warning" | "info";

export interface ToastMessage {
    id: string;
    type: ToastType;
    message: string;
    duration?: number;
}

interface ToastProps {
    toast: ToastMessage;
    onClose: (id: string) => void;
}

function Toast({ toast, onClose }: ToastProps) {
    const [isLeaving, setIsLeaving] = useState(false);

    useEffect(() => {
        const duration = toast.duration ?? 5000;
        const timer = setTimeout(() => {
            setIsLeaving(true);
            setTimeout(() => onClose(toast.id), 300);
        }, duration);

        return () => clearTimeout(timer);
    }, [toast, onClose]);

    const handleClose = () => {
        setIsLeaving(true);
        setTimeout(() => onClose(toast.id), 300);
    };

    const iconMap = {
        success: faCheckCircle,
        error: faExclamationTriangle,
        warning: faExclamationTriangle,
        info: faInfoCircle
    };

    const colorMap = {
        success: "border-emerald-500/30 bg-emerald-500/10 text-emerald-100",
        error: "border-red-500/30 bg-red-500/10 text-red-100",
        warning: "border-amber-500/30 bg-amber-500/10 text-amber-100",
        info: "border-brand-primary/30 bg-brand-primary/10 text-white"
    };

    const iconColorMap = {
        success: "text-emerald-400",
        error: "text-red-400",
        warning: "text-amber-400",
        info: "text-brand-secondary"
    };

    return (
        <div
            className={clsx(
                "flex items-center gap-3 rounded-2xl border px-4 py-3 shadow-soft backdrop-blur-xl transition-all duration-300",
                colorMap[toast.type],
                isLeaving ? "translate-x-full opacity-0" : "translate-x-0 opacity-100"
            )}
        >
            <FontAwesomeIcon
                icon={iconMap[toast.type]}
                className={clsx("h-5 w-5 flex-shrink-0", iconColorMap[toast.type])}
            />
            <p className="flex-1 text-sm font-medium">{toast.message}</p>
            <button
                type="button"
                onClick={handleClose}
                className="flex-shrink-0 text-white/60 transition hover:text-white"
                aria-label="Fechar notificação"
            >
                <FontAwesomeIcon icon={faXmark} className="h-4 w-4" />
            </button>
        </div>
    );
}

export function ToastContainer() {
    const [toasts, setToasts] = useState<ToastMessage[]>([]);

    useEffect(() => {
        const handleToast = (event: CustomEvent<Omit<ToastMessage, "id">>) => {
            const newToast: ToastMessage = {
                ...event.detail,
                id: `toast-${Date.now()}-${Math.random()}`
            };
            setToasts((prev) => [...prev, newToast]);
        };

        window.addEventListener("show-toast", handleToast as EventListener);
        return () => window.removeEventListener("show-toast", handleToast as EventListener);
    }, []);

    const handleClose = (id: string) => {
        setToasts((prev) => prev.filter((toast) => toast.id !== id));
    };

    if (toasts.length === 0) {
        return null;
    }

    return (
        <div className="pointer-events-none fixed right-4 top-28 z-[100] flex max-w-md flex-col gap-3">
            {toasts.map((toast) => (
                <div key={toast.id} className="pointer-events-auto">
                    <Toast toast={toast} onClose={handleClose} />
                </div>
            ))}
        </div>
    );
}

// Helper function to show toast
export function showToast(
    message: string,
    type: ToastType = "info",
    duration?: number
) {
    if (typeof window !== "undefined") {
        window.dispatchEvent(
            new CustomEvent("show-toast", {
                detail: { message, type, duration }
            })
        );
    }
}
